import { Edge, Viewport } from '@xyflow/react';
import { atom } from 'jotai';

import {
  type AutomationNode,
  type AutomationNodeData,
} from '../_components/editor/nodes/types';
import { automationCreateAtom } from '../_graphql/mutationAutomationCreate';
import { automationUpdateAtom } from '../_graphql/mutationAutomationUpdate';
import { automationDetailQueryAtom } from '../_graphql/queryAutomation';
import { integrationsQueryAtom } from '../_graphql/queryIntegrations';
import { automationsRoutes } from '../routes';
import { TriggerType } from '../types';

import { atomFromRoutePath } from '~/shared/state/location.state';

const $detailIdFromRoute = atomFromRoutePath(automationsRoutes.detail);
const $createModeRoute = atomFromRoutePath(automationsRoutes.create);

export const $isCreateMode = atom((get) => get($createModeRoute).__didMatch);

export const $editorAutomationId = atom((get) => {
  // In create mode, there's no automation ID
  if (get($isCreateMode)) return undefined;
  const params = get($detailIdFromRoute) as { id?: string } | null;
  return params?.id ? String(params.id) : undefined;
});

export type EditorTab = 'flow' | 'history' | 'versions';
export const $editorTab = atom<EditorTab>('flow');

export const $automationName = atom('');
export const $automationDescription = atom('');
export const $createAutomationTriggerType = atom<TriggerType | null>(null);

// Track which automation the editor state was initialized for
// null = not initialized, 'create' = create mode, string = automation ID
export const $editorInitializedForId = atom<string | 'create' | null>(null);

const $originalName = atom('');
const $originalDescription = atom('');

export const $resetCreateMode = atom(null, (_get, set) => {
  set($automationName, '');
  set($automationDescription, '');
  set($createAutomationTriggerType, null);
  set($editorInitializedForId, 'create'); // Mark as initialized for create mode
});

export const $createAutomation = automationCreateAtom({});

export const $createAutomationName = $automationName;
export const $createAutomationDescription = $automationDescription;

export const $remoteEditorAutomation = automationDetailQueryAtom((get) => {
  const id = get($editorAutomationId);
  return {
    variables: { id: id ?? '' },
    skip: !id,
  };
});

export const $integrations = integrationsQueryAtom(() => ({}));

export const $editorNodes = atom<AutomationNode[]>([]);
export const $editorEdges = atom<Edge[]>([]);
export const $selectedNodeId = atom<string | null>(null);

// Multi-selection state
export const $selectedNodeIds = atom<Set<string>>(new Set<string>());

// Computed: array of selected nodes
export const $selectedNodes = atom((get) => {
  const selectedIds = get($selectedNodeIds);
  const nodes = get($editorNodes);
  return nodes.filter((node) => selectedIds.has(node.id));
});

// Actions
export const $setSelectedNodeIds = atom(
  null,
  (_get, set, ids: Set<string> | string[]) => {
    set($selectedNodeIds, ids instanceof Set ? ids : new Set(ids));
  },
);

export const $addToSelection = atom(null, (get, set, nodeId: string) => {
  const current = get($selectedNodeIds);
  const newSet = new Set(current);
  newSet.add(nodeId);
  set($selectedNodeIds, newSet);
});

export const $removeFromSelection = atom(null, (get, set, nodeId: string) => {
  const current = get($selectedNodeIds);
  const newSet = new Set(current);
  newSet.delete(nodeId);
  set($selectedNodeIds, newSet);
});

export const $toggleNodeSelection = atom(null, (get, set, nodeId: string) => {
  const current = get($selectedNodeIds);
  const newSet = new Set(current);
  if (newSet.has(nodeId)) {
    newSet.delete(nodeId);
  } else {
    newSet.add(nodeId);
  }
  set($selectedNodeIds, newSet);
});

export const $clearSelection = atom(null, (_get, set) => {
  set($selectedNodeIds, new Set<string>());
});

const $hasFlowChanges = atom(false);
const $hasMetadataChanges = atom(false);

const VIEWPORT_STORAGE_KEY = 'automation-viewport';

const getStoredViewport = (automationId: string): Viewport | null => {
  try {
    const stored = localStorage.getItem(
      `${VIEWPORT_STORAGE_KEY}:${automationId}`,
    );
    if (stored) {
      const parsed = JSON.parse(stored);
      if (
        typeof parsed.x === 'number' &&
        typeof parsed.y === 'number' &&
        typeof parsed.zoom === 'number'
      ) {
        return parsed as Viewport;
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
};

const storeViewport = (automationId: string, viewport: Viewport): void => {
  try {
    localStorage.setItem(
      `${VIEWPORT_STORAGE_KEY}:${automationId}`,
      JSON.stringify(viewport),
    );
  } catch {
    // localStorage may be unavailable
  }
};

export const $editorViewport = atom<Viewport | null>(null);

export const $loadViewport = atom(null, (get, set) => {
  const automationId = get($editorAutomationId);
  if (automationId) {
    const stored = getStoredViewport(automationId);
    set($editorViewport, stored);
  } else {
    set($editorViewport, null);
  }
});

export const $saveViewport = atom(null, (get, _set, viewport: Viewport) => {
  const automationId = get($editorAutomationId);
  if (automationId) {
    storeViewport(automationId, viewport);
  }
});

export const $hasUnsavedChanges = atom(
  (get) => get($hasFlowChanges) || get($hasMetadataChanges),
  (_get, set, value: boolean) => {
    set($hasFlowChanges, value);
    set($hasMetadataChanges, value);
  },
);

export const $resetEditor = atom(null, (_get, set) => {
  set($editorInitializedForId, null); // Mark as not initialized
  set($editorNodes, []);
  set($editorEdges, []);
  set($editorViewport, null);
  set($selectedNodeId, null);
  set($selectedNodeIds, new Set());
  set($hasFlowChanges, false);
  set($hasMetadataChanges, false);
  set($editorTab, 'flow');
  set($automationName, '');
  set($automationDescription, '');
  set($originalName, '');
  set($originalDescription, '');
});

export const $initializeEditor = atom(null, (get, set) => {
  const automationId = get($editorAutomationId);
  const automation = get($remoteEditorAutomation).data;
  if (automation) {
    set($editorNodes, automation.nodes || []);
    set($editorEdges, automation.edges || []);
    set($automationName, automation.name || '');
    set($automationDescription, automation.description || '');
    set($originalName, automation.name || '');
    set($originalDescription, automation.description || '');
    set($hasFlowChanges, false);
    set($hasMetadataChanges, false);
    set($editorInitializedForId, automationId ?? 'create'); // Mark as initialized
  }
});

export const $initializeEditorWithData = atom(
  null,
  (
    _get,
    set,
    data: {
      nodes: AutomationNode[] | null | undefined;
      edges: Edge[] | null | undefined;
    },
  ) => {
    set($editorNodes, data.nodes || []);
    set($editorEdges, data.edges || []);
    set($hasUnsavedChanges, false);
  },
);

export const $setEditorNodes = atom(
  null,
  (
    get,
    set,
    nodesOrUpdater:
      | AutomationNode[]
      | ((prev: AutomationNode[]) => AutomationNode[]),
    markAsChanged = true,
  ) => {
    const newNodes =
      typeof nodesOrUpdater === 'function'
        ? nodesOrUpdater(get($editorNodes))
        : nodesOrUpdater;
    set($editorNodes, newNodes);
    if (markAsChanged) {
      set($hasFlowChanges, true);
    }
  },
);

export const $setEditorEdges = atom(
  null,
  (get, set, edgesOrUpdater: Edge[] | ((prev: Edge[]) => Edge[])) => {
    const newEdges =
      typeof edgesOrUpdater === 'function'
        ? edgesOrUpdater(get($editorEdges))
        : edgesOrUpdater;
    set($editorEdges, newEdges);
    set($hasFlowChanges, true);
  },
);

export const $setAutomationName = atom(null, (get, set, name: string) => {
  set($automationName, name);
  const original = get($originalName);
  set(
    $hasMetadataChanges,
    name !== original ||
      get($automationDescription) !== get($originalDescription),
  );
});

export const $setAutomationDescription = atom(
  null,
  (get, set, description: string) => {
    set($automationDescription, description);
    const original = get($originalDescription);
    set(
      $hasMetadataChanges,
      description !== original || get($automationName) !== get($originalName),
    );
  },
);

export const $saveFlow = automationUpdateAtom({
  onSuccess: (get, set) => {
    set($hasFlowChanges, false);
    set($hasMetadataChanges, false);
    set($originalName, get($automationName));
    set($originalDescription, get($automationDescription));
  },
});

export const $selectedNode = atom((get) => {
  const selectedId = get($selectedNodeId);
  const nodes = get($editorNodes);
  return nodes.find((node) => node.id === selectedId) || null;
});

export const $updateSelectedNode = atom(
  null,
  (get, set, updates: Partial<AutomationNodeData>) => {
    const selectedId = get($selectedNodeId);
    if (!selectedId) return;

    const nodes = get($editorNodes);
    const currentNode = nodes.find((n) => n.id === selectedId);
    if (!currentNode) return;

    const newData = { ...currentNode.data, ...updates };
    if (JSON.stringify(currentNode.data) === JSON.stringify(newData)) return;

    const updatedNodes = nodes.map((node) =>
      node.id === selectedId ? { ...node, data: newData } : node,
    );

    set($editorNodes, updatedNodes);
    set($hasFlowChanges, true);
  },
);
